import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Card from 'emerald-ui/lib/Card';
import Button from 'emerald-ui/lib/Button';
import Icon from 'emerald-ui/lib/Icon';
import SearchForm from 'emerald-ui/lib/SearchForm';
import OverlayTrigger from 'emerald-ui/lib/OverlayTrigger';
import Popover from 'emerald-ui/lib/Popover';
import { Link, navigate } from 'gatsby';
import classnames from 'classnames';

import emeraldLogo from '../../images/emerald.svg';
import emeraldLogoWhite from '../../images/emerald-logo-white.svg';
import seedData from '../../data/seed';

import { createKeybindingsHandler } from 'tinykeys';
import IconButton from 'emerald-ui/lib/IconButton';

import { useThemeStore } from '../../state/themeStore';

function Sidebar(props) {
  const [currentComponent] = useState(seedData.find((item) => item.name === props.currentPage) || seedData[0]);
  const [searchValue, setSearchValue] = useState('');
  const [show, setShow] = useState(false);

  const setInitialTheme = useThemeStore((state) => state.setInitialTheme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);
  const theme = useThemeStore((state) => state.theme);

  const sidebarMenuNode = useRef(null);
  const searchFormNode = useRef(null);

  useEffect(() => {
    if (setInitialTheme) {
      setInitialTheme();
    }

    const handler = createKeybindingsHandler({
      'Alt+KeyF': focusSearchForm,
      'Alt+KeyN': goToFirstComponent,
      'Alt+KeyK': toggleTheme,
    });

    window.addEventListener('keydown', handler);

    sidebarMenuNode.current.scrollTop = getFromSessionStorage('eui-docs-menu-scroll');

    sidebarMenuNode.current.addEventListener('scroll', () => {
      const scrollValue = sidebarMenuNode.current.scrollTop;

      setSessionStorage('eui-docs-menu-scroll', scrollValue);
    });

    const sessionStorageSearchValue = getFromSessionStorage('eui-docs-search-value');

    setSearchValue(sessionStorageSearchValue || '');

    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [setInitialTheme, toggleTheme]);

  useEffect(() => {
    setSessionStorage('eui-docs-search-value', searchValue);
  }, [searchValue]);

  function focusSearchForm(event) {
    event.preventDefault();
    searchFormNode.current.focus();
  }

  function goToFirstComponent() {
    if (window.location.pathname === '/' || window.location.pathname === '/emerald-ui/docs/') {
      navigate(`/${seedData[0].name}`);
    }
  }

  function handleSearchChange(event) {
    const value = event.target.value;

    setSearchValue(value);
  }

  function getFromSessionStorage(name) {
    return sessionStorage.getItem(name);
  }

  function setSessionStorage(name, value) {
    sessionStorage.setItem(name, value);
  }

  function handleSearchKeyDown(event) {
    if (event.keyCode === 13) {
      const currentComponent = filterComponents(seedData, searchValue)[0];

      if (currentComponent && currentComponent.name) {
        navigate(`/${currentComponent.name}`);
        handleSearchClear();
      }
    }
  }

  function handleSearchClear() {
    setSearchValue('');
    setSessionStorage('eui-docs-search-value', '');
  }

  function filterComponents(data, keyword) {
    return data.filter((item) => {
      return item.name.toLowerCase().includes(keyword.toLowerCase());
    });
  }

  function toggleBar() {
    setShow(!show);
  }

  const componentItems = filterComponents(seedData, searchValue ? searchValue.toLowerCase() : '').map((item, index) => {
    return (
      <li key={`item-${index}`}>
        <Link
          id={item.name}
          className={classnames({
            active: !props.currentPage ? false : item.name === currentComponent.name,
          })}
          to={`/${item.name}`}
        >
          <span>{item.name}</span>
        </Link>
      </li>
    );
  });

  function handleSubmit(event) {
    event.preventDefault();
  }

  return (
    <Card
      className={classnames('sidebar', props.className, {
        show,
      })}
    >
      <Button onClick={toggleBar} shape="outline" className="toggle outside">
        <Icon name="menu" />
      </Button>
      <Button onClick={toggleBar} shape="outline" className="toggle inside">
        <Icon name="close" />
      </Button>
      <div className="logo-section">
        <div className="logo-and-keyboard">
          <Link to="/" className="logo" style={{ textAlign: 'center' }}>
            <img src={theme === 'dark' ? emeraldLogoWhite : emeraldLogo} alt="Emerald-UI Logo" />
          </Link>
          <OverlayTrigger
            style={{ margin: '3px' }}
            placement="bottom"
            overlay={
              <Popover title="Keyboard shortcuts" className={`kb-shortcuts ${theme}`}>
                <ul>
                  <li>
                    <span>Toggle Dark Mode</span>
                    <span>Alt+K</span>
                  </li>
                  <li>
                    <span>Focus the Search field</span>
                    <span>Alt+F</span>
                  </li>
                  <li>
                    <span>Show the next component</span>
                    <span>Alt+N</span>
                  </li>
                  <li>
                    <span>Show the previous component</span>
                    <span>Alt+B</span>
                  </li>
                  <li>
                    <span>Go to the Home page</span>
                    <span>Alt+H</span>
                  </li>
                  <li>
                    <span>Open the Github page</span>
                    <span>g g h</span>
                  </li>
                  <li>
                    <span>Open the Storybook page</span>
                    <span>g s b</span>
                  </li>
                  <li>
                    <span>Credits</span>
                    <span>The KONAMI code</span>
                  </li>
                </ul>
              </Popover>
            }
          >
            <IconButton icon="keyboard" />
          </OverlayTrigger>
        </div>
        <SearchForm
          value={searchValue}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          clearable
          onClear={handleSearchClear}
          onSubmit={handleSubmit}
          ref={searchFormNode}
        />
        <br />
      </div>
      <div className="sidebar-menu" ref={sidebarMenuNode}>
        <ul className="component-items">{componentItems}</ul>
      </div>
    </Card>
  );
}

Sidebar.propTypes = {
  currentPage: PropTypes.string,
  className: PropTypes.string,
};

export default Sidebar;
